import { toast } from 'react-toastify';
import { NOTIFICATION_CONFIG } from './notificationConfig';

/**
 * Centralized Notification Service
 * Wraps react-toastify with standardized API and configuration
 */
class NotificationService {

  /**
   * Show success notification
   * @param {string} message - The message to display
   * @param {object} options - Optional toast options to override defaults
   * @returns {number|string} Toast ID
   */
  success(message, options = {}) {
    return toast.success(message, {
      ...NOTIFICATION_CONFIG.success,
      ...options
    });
  }

  /**
   * Show error notification
   * @param {string|object} error - Error message or error object
   * @param {object} options - Optional toast options to override defaults
   * @returns {number|string} Toast ID
   */
  error(error, options = {}) {
    const message = this._extractErrorMessage(error);
    return toast.error(message, {
      ...NOTIFICATION_CONFIG.error,
      ...options
    });
  }

  /**
   * Show warning notification
   * @param {string} message - The message to display
   * @param {object} options - Optional toast options to override defaults
   * @returns {number|string} Toast ID
   */
  warning(message, options = {}) {
    return toast.warning(message, {
      ...NOTIFICATION_CONFIG.warning,
      ...options
    });
  }

  /**
   * Show info notification
   * @param {string} message - The message to display
   * @param {object} options - Optional toast options to override defaults
   * @returns {number|string} Toast ID
   */
  info(message, options = {}) {
    return toast.info(message, {
      ...NOTIFICATION_CONFIG.info,
      ...options
    });
  }

  /**
   * Show a loading notification that can be updated later
   * @param {string} message - The loading message
   * @returns {number|string} Toast ID for updating
   */
  loading(message = 'Loading...') {
    return toast.loading(message, NOTIFICATION_CONFIG.loading);
  }

  /**
   * Update an existing toast (useful for loading states)
   * @param {number|string} toastId - The toast ID to update
   * @param {object} config - Update configuration
   * @returns {void}
   */
  update(toastId, config) {
    return toast.update(toastId, config);
  }

  /**
   * Dismiss a specific toast
   * @param {number|string} toastId - The toast ID to dismiss
   * @returns {void}
   */
  dismiss(toastId) {
    return toast.dismiss(toastId);
  }

  /**
   * Dismiss all toasts
   * @returns {void}
   */
  dismissAll() {
    return toast.dismiss();
  }

  /**
   * Extract error message from various error formats
   * Handles API error responses, Error objects, and strings
   * @param {string|object} error - The error to extract message from
   * @returns {string} Extracted error message
   * @private
   */
  _extractErrorMessage(error) {
    // String message
    if (typeof error === 'string') {
      return error;
    }

    // API error response (Axios format)
    if (error?.response?.data) {
      const data = error.response.data;

      // Try different common API error message fields
      if (data.message) return data.message;
      if (data.error) return data.error;
      if (data.detail) return data.detail;
      if (data.msg) return data.msg;
      if (data.Message) return data.Message;

      // Handle array of errors
      if (data.errors) {
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          return data.errors[0];
        }
        if (data.errors.non_field_errors && Array.isArray(data.errors.non_field_errors)) {
          return data.errors.non_field_errors[0];
        }
        if (typeof data.errors === 'object') {
          const messages = Object.values(data.errors)
            .flatMap(v => (Array.isArray(v) ? v : [v]))
            .filter(v => v && typeof v === 'string');
          if (messages.length > 0) return messages.join(' | ');
        }
      }

      // Handle redirect_url field (specific to this app)
      if (data.redirect_url && Array.isArray(data.redirect_url)) {
        return data.redirect_url[0];
      }

      // If data itself is an array
      if (Array.isArray(data) && data.length > 0) {
        return data[0];
      }

      // Handle field-level errors: { email: ["msg"], mobile_number: ["msg"] }
      if (typeof data === 'object' && data !== null) {
        const fieldError = Object.values(data)
          .map(v => (Array.isArray(v) ? v[0] : v))
          .find(v => v && typeof v === 'string');
        if (fieldError) return fieldError;
      }
    }

    // Error object with message property
    if (error?.message) {
      return error.message;
    }

    // Fallback for unknown error formats
    return 'An unexpected error occurred';
  }

  /**
   * Remove special characters from message
   * Useful for sanitizing API error messages
   * @param {string} message - Message to sanitize
   * @returns {string} Sanitized message
   */
  sanitize(message) {
    if (typeof message !== 'string') {
      return message;
    }
    return message.replace(/[~`!@#$%^&*(){}\[\]"'\/\\]/g, '');
  }
}

// Export singleton instance
export default new NotificationService();
